<template>
  <div class="activity-admin-container">
    <!-- 顶部服务器选择栏 -->
    <div class="top-bar">
      <div class="top-bar-left">
        <span class="label-text">目标服务器：</span>
        <el-select
          v-model="currentServer"
          placeholder="请选择服务器"
          @change="onServerChange"
          style="width: 280px"
          size="default"
        >
          <el-option
            v-for="srv in serverList"
            :key="srv.key"
            :label="srv.name"
            :value="srv.key"
          />
        </el-select>
      </div>
      <div class="top-bar-right">
        <span class="page-title">礼包活动配置管理</span>
      </div>
    </div>

    <el-container class="main-container">
      <!-- 左侧活动侧边栏【深色侧边栏】 -->
      <el-aside width="280px" class="aside-wrap">
        <div class="aside-toolbar">
          <el-button type="primary" icon="Plus" @click="openAddDialog" full-width>
            新增礼包活动
          </el-button>
        </div>
        <el-divider margin="12px" />
        <el-scrollbar height="calc(100% - 90px)">
          <div v-if="activityLoading" class="list-loading">
            <el-spinner size="24" />
            <p>加载中...</p>
          </div>
          <el-list v-else>
            <el-list-item
              v-for="item in activityList"
              :key="item.activity.activity_id"
              :class="['list-item', { active: selectActivityId === item.activity.activity_id }]"
              @click="selectActivity(item)"
            >
              <span class="item-name">{{ item.activity.activity_name }}</span>
            </el-list-item>
          </el-list>
          <el-empty
            v-if="!activityLoading && activityList.length === 0"
            description="当前服务器暂无活动数据"
            image-size="80"
          />
        </el-scrollbar>
      </el-aside>

      <!-- 右侧详情区域 -->
      <el-main class="main-wrap">
        <div v-if="currentActivity" class="detail-wrap">
          <div class="detail-header">
            <h3 class="detail-title">活动详情</h3>
            <div class="btn-group">
              <el-button type="warning" icon="Edit" @click="openEditDialog">编辑活动</el-button>
              <el-button type="danger" icon="Delete" @click="handleDelete">删除活动</el-button>
            </div>
          </div>

          <!-- 卡片横向布局 -->
          <div class="card-row">
            <!-- 基础信息卡片 -->
            <el-card shadow="hover" class="info-card">
              <template #header>
                <span class="card-header-title">活动基础信息</span>
              </template>
              <el-descriptions border column="3">
                <el-descriptions-item label="活动ID">{{ currentActivity.activity.activity_id }}</el-descriptions-item>
                <el-descriptions-item label="活动名称">{{ currentActivity.activity.activity_name }}</el-descriptions-item>
                <el-descriptions-item label="活动类型">
                  {{ activityTypeMap[currentActivity.activity.activity_type] || '未知类型' }}
                </el-descriptions-item>
                <el-descriptions-item label="售价(分)">{{ currentActivity.activity.sell_price }}</el-descriptions-item>
                <el-descriptions-item label="单人限购次数">{{ currentActivity.activity.limit_count }}</el-descriptions-item>
                <el-descriptions-item label="活动状态">
                  <el-tag :type="getStatusTagType(currentActivity.activity.status)">
                    {{ statusMap[currentActivity.activity.status] || '未知' }}
                  </el-tag>
                </el-descriptions-item>
                <el-descriptions-item label="开始时间">{{ currentActivity.activity.start_datetime || '-' }}</el-descriptions-item>
                <el-descriptions-item label="结束时间">{{ currentActivity.activity.end_datetime || '-' }}</el-descriptions-item>
                <el-descriptions-item label="运营备注" :span="3">{{ currentActivity.activity.remark || '-' }}</el-descriptions-item>
              </el-descriptions>
            </el-card>

            <!-- 奖励列表卡片 -->
            <el-card shadow="hover" class="reward-card">
              <template #header>
                <span class="card-header-title">活动奖励配置</span>
              </template>
              <el-table :data="currentActivity.rewardList" border stripe :key="selectActivityId">
                <el-table-column prop="itemName" label="道具名称" align="center" />
                <el-table-column prop="itemNum" label="道具数量" align="center" />
              </el-table>
            </el-card>
          </div>
        </div>

        <div v-else class="empty-select">
          <el-empty description="请在左侧列表选择一项活动查看详情" image-size="120" />
        </div>
      </el-main>
    </el-container>

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      v-model="dialogVisible"
      :title="isEdit ? '编辑礼包活动' : '新增礼包活动'"
      width="820px"
      destroy-on-close
    >
      <el-form ref="formRef" :model="formData" label-width="120px">
        <el-row :gutter="18">
          <el-col span="12">
            <el-form-item label="活动名称" prop="activityName">
              <el-input v-model="formData.activityName" placeholder="请输入活动名称" />
            </el-form-item>
          </el-col>
          <el-col span="12">
            <el-form-item label="活动类型" prop="activityType">
              <div style="display:flex;align-items:center;gap:12px;">
                <el-select v-model="formData.activityType" placeholder="选择类型" @change="onTypeChange" style="flex:1">
                  <el-option label="一次性限购礼包" :value="1" />
                  <el-option label="限时可多次购买礼包" :value="2" />
                </el-select>
                <span style="width:180px;height:36px;border:1px solid #dcdfe6;border-radius:4px;padding:0 10px;display:flex;align-items:center;background:#f5f7fa;">
                  {{ activityTypeMap[formData.activityType] || '' }}
                </span>
              </div>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="18">
          <el-col span="12">
            <el-form-item label="售价（单位：分）" prop="sellPrice">
              <el-input-number v-model="formData.sellPrice" :min="0" style="width:100%" />
            </el-form-item>
          </el-col>
          <el-col span="12">
            <el-form-item label="单人限购次数" prop="limitCount">
              <el-input-number
                v-model="formData.limitCount"
                :min="0"
                style="width:100%"
                :disabled="formData.activityType === 1"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="18">
          <el-col span="12">
            <el-form-item label="活动状态" prop="status">
              <div style="display:flex;align-items:center;gap:12px;">
                <el-select v-model="formData.status" style="flex:1">
                  <el-option label="禁用" :value="0" />
                  <el-option label="正常开启" :value="1" />
                  <el-option label="手动关闭" :value="2" />
                </el-select>
                <span style="width:180px;height:36px;border:1px solid #dcdfe6;border-radius:4px;padding:0 10px;display:flex;align-items:center;background:#f5f7fa;">
                  {{ statusMap[formData.status] || '' }}
                </span>
              </div>
            </el-form-item>

          </el-col>
        </el-row>
        <el-form-item label="活动时间区间" prop="dateRange">
          <el-date-picker
            v-model="formData.dateRange"
            type="datetimerange"
            range-separator="至"
            start-placeholder="选择开始时间"
            end-placeholder="选择结束时间"
            format="YYYY-MM-DD HH:mm:ss"
            value-format="YYYY-MM-DD HH:mm:ss"
            style="width:100%"
          />
        </el-form-item>
        <el-form-item label="运营备注">
          <el-input v-model="formData.remark" type="textarea" rows="3" placeholder="可选，填写运营说明" />
        </el-form-item>

        <el-divider content-position="left">奖励道具配置</el-divider>
        <div class="table-toolbar">
          <el-button size="small" type="primary" icon="Plus" @click="addRewardLine">添加道具行</el-button>
        </div>
        <el-table :data="formData.rewardList" border stripe>
          <el-table-column label="道具名称" align="center">
            <template #default="scope">
              <el-input v-model="scope.row.itemName" size="small" placeholder="道具名称" />
            </template>
          </el-table-column>
          <el-table-column label="道具数量" align="center">
            <template #default="scope">
              <el-input-number v-model="scope.row.itemNum" :min="1" size="small" />
            </template>
          </el-table-column>
          <el-table-column label="操作" width="90" align="center">
            <template #default="scope">
              <el-button size="small" danger text icon="Delete" @click="removeRewardLine(scope.$index)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitForm" :loading="submitLoading">保存提交</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axios from 'axios'

const baseUrl = 'https://log.wuxiahaojie.com/api/ActivityAdminService'
const activityTypeMap = {
  1: '一次性限购礼包',
  2: '限时可多次购买礼包'
}
const statusMap = {
  0: '禁用',
  1: '正常开启',
  2: '手动关闭'
}

const serverList = ref([])
const currentServer = ref('')

const activityLoading = ref(false)
const activityList = ref([])
const selectActivityId = ref(null)
const currentActivity = ref(null)

const dialogVisible = ref(false)
const isEdit = ref(false)
const submitLoading = ref(false)
const formRef = ref(null)

const formData = reactive({
  activityId: 0,
  activityName: '',
  activityType: 1,
  dateRange: [],
  sellPrice: 0,
  limitCount: 1,
  status: 1,
  remark: '',
  oldVersion: 0,
  rewardList: []
})

function onTypeChange(typeVal) {
  if (typeVal === 1) {
    formData.limitCount = 1
  }
}

onMounted(async () => {
  await loadServerList()
  if (serverList.value.length > 0) {
    loadActivityList()
  }
})

async function loadServerList() {
  try {
    const token = sessionStorage.getItem('token')
    const res = await axios.get(`${baseUrl}/GetServerNameList`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    console.log(res)
    const strList = res.data || []
    serverList.value = strList.map(s => ({
      key: s,
      name: s
    }))
    if (serverList.value.length > 0) {
      currentServer.value = serverList.value[0].key
      loadActivityList()
    }
  } catch (err) {
    ElMessage.error('服务器列表请求异常：' + err.message)
  }
}

function onServerChange() {
  selectActivityId.value = null
  currentActivity.value = null
  loadActivityList()
}

async function loadActivityList() {
  if (!currentServer.value) return
  activityLoading.value = true
  try {
    const token = sessionStorage.getItem('token')
    const res = await axios.get(`${baseUrl}/GetAllActivityList`, {
      params: { serverName: currentServer.value },
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    if (res.data.code === 200) {
      const dtoList = res.data.data || []
      activityList.value = dtoList.map(dto => {
        return {
          activity: {
            activity_id: dto.activityId,
            activity_name: dto.activityName,
            activity_type: dto.activityType,
            start_datetime: dto.startDateTime,
            end_datetime: dto.endDateTime,
            sell_price: dto.sellPrice,
            limit_count: dto.limitCount,
            status: dto.status,
            version: dto.version,
            remark: dto.remark
          },
          rewardList: dto.rewardList || []
        }
      })
    } else {
      ElMessage.error(res.data.msg)
    }
  } catch (err) {
    ElMessage.error('列表加载失败：' + err.message)
  } finally {
    activityLoading.value = false
  }
}

function selectActivity(item) {
  selectActivityId.value = item.activity.activity_id
  currentActivity.value = JSON.parse(JSON.stringify(item))
}

function openAddDialog() {
  isEdit.value = false
  resetForm()
  dialogVisible.value = true
}

function openEditDialog() {
  if (!currentActivity.value) return
  isEdit.value = true
  dialogVisible.value = true
  const act = currentActivity.value.activity

  formData.activityId = act.activity_id
  formData.activityName = act.activity_name
  formData.activityType = act.activity_type
  formData.sellPrice = act.sell_price
  formData.limitCount = act.limit_count
  formData.status = act.status
  formData.remark = act.remark
  formData.oldVersion = act.version

  if (act.start_datetime && act.end_datetime) {
    formData.dateRange = [act.start_datetime, act.end_datetime]
  } else {
    formData.dateRange = []
  }
  formData.rewardList = JSON.parse(JSON.stringify(currentActivity.value.rewardList))
}

async function submitForm() {
  if (!formData.dateRange || formData.dateRange.length !== 2) {
    ElMessage.warning('请选择活动起止时间')
    return
  }
  if (formData.rewardList.length === 0) {
    ElMessage.warning('至少配置一条奖励道具')
    return
  }

  submitLoading.value = true
  try {
    const payload = {
      ActivityId: formData.activityId,
      ActivityName: formData.activityName,
      ActivityType: formData.activityType,
      StartDateStr: formData.dateRange[0],
      EndDateStr: formData.dateRange[1],
      SellPrice: formData.sellPrice,
      LimitCount: formData.limitCount,
      Status: formData.status,
      Remark: formData.remark,
      OldVersion: formData.oldVersion,
      RewardList: formData.rewardList
    }

    const token = sessionStorage.getItem('token')
    const res = await axios.post(`${baseUrl}/SaveActivity`, payload, {
      params: { servername: currentServer.value },
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    if (res.data.code === 200) {
      ElMessage.success(res.data.msg || '保存成功')
      dialogVisible.value = false
      await loadActivityList()
      currentActivity.value = null
      selectActivityId.value = null
    } else {
      ElMessage.error(res.data.msg)
    }
  } catch (e) {
    ElMessage.error('请求异常：' + e.message)
  } finally {
    submitLoading.value = false
  }
}

async function handleDelete() {
  if (!currentActivity.value) return
  try {
    await ElMessageBox.confirm('确定删除该活动？删除后无法恢复！', '警告', {
      type: 'warning'
    })
  } catch {
    // 用户点取消，直接退出函数
    return
  }

  try {
    const aid = currentActivity.value.activity.activity_id
    const params = {
      servername: currentServer.value,
      activityId: aid
    }
    const token = sessionStorage.getItem('token')
    const res = await axios.post(`${baseUrl}/DeleteActivity`, params, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    if (res.data.code === 200) {
      ElMessage.success('删除成功')
      await loadActivityList()
      currentActivity.value = null
      selectActivityId.value = null
    } else {
      ElMessage.error(res.data.msg)
    }
  } catch (err) {
    ElMessage.error('删除失败：' + err.message)
  }
}


function addRewardLine() {
  formData.rewardList.push({ itemName: '', itemNum: 1 })
}

function removeRewardLine(index) {
  formData.rewardList.splice(index, 1)
}

function resetForm() {
  formData.activityId = 0
  formData.activityName = ''
  formData.activityType = 1
  formData.dateRange = []
  formData.sellPrice = 0
  formData.limitCount = 1
  formData.status = 1
  formData.remark = ''
  formData.oldVersion = 0
  formData.rewardList = []
}

function getStatusTagType(v) {
  const map = {
    0: 'danger',
    1: 'success',
    2: 'warning'
  }
  return map[v] || ''
}
</script>

<style scoped>
.activity-admin-container {
  height: calc(100vh - 16px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  background: rgba(255,255,255,0.75);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid rgba(220, 230, 240, 0.6);
}
.label-text {
  font-size: 14px;
  color: #303133;
  margin-right: 10px;
}
.page-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}
.main-container {
  flex: 1;
}
.aside-wrap {
  background: #24344b;
  padding: 20px 14px;
}
.aside-toolbar {
  padding: 0 4px;
  margin-bottom:16px;
}
:deep(.el-list-item) {
  padding: 0 !important;
  border: none !important;
  background: transparent !important;
}
:deep(.el-list-item__content) {
  width: 100%;
  padding: 12px 16px;
  border-radius: 8px;
  transition: background 0.24s ease;
  color: #f1f5f9;
  cursor: pointer;
}
.list-item:hover :deep(.el-list-item__content) {
  background-color: #364b68;
}
.list-item.active :deep(.el-list-item__content) {
  background-color: #3388ee;
  color: #ffffff;
}
.item-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display:block;
}
.list-loading {
  text-align: center;
  padding: 40px 0;
  color: #94a3b8;
}
.main-wrap {
  background: linear-gradient(120deg,#edf2f9,#dbeafe);
  padding: 24px;
}
.detail-wrap {
  height: 100%;
}
.detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}
.detail-title {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: #1e293b;
}
.btn-group {
  display: flex;
  gap: 12px;
}
.card-row {
  display: grid;
  grid-template-columns: 62% 36%;
  gap: 24px;
}
.info-card,
.reward-card {
  border-radius: 16px;
  background: rgba(255,255,255,0.82);
  backdrop-filter: blur(10px);
}
.card-header-title {
  font-weight: 600;
  font-size: 16px;
  color: #1e293b;
}
.empty-select {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.table-toolbar {
  margin-bottom: 12px;
}
.dialog-footer {
  text-align: right;
}
</style>
